import type { ReactNode } from "react";

interface Props {
  badge: string;
  title: string;
  subtitle?: string;
  footer?: string;
  accent?: string;
  selected?: boolean;
  onClick?: () => void;
  dir?: "ltr" | "rtl";
  icon?: ReactNode;
}

// Selection card with a huge faded letter/number in the corner.
// See designs/2026-04-20-redesign-handoff/noteornot/project/game-core.jsx PuzzleCard.
export function PuzzleCard({
  badge,
  title,
  subtitle,
  footer,
  accent = "#B658FF",
  selected = false,
  onClick,
  dir = "ltr",
}: Props) {
  const cornerKey = dir === "rtl" ? "left" : "right";
  const oppositeKey = dir === "rtl" ? "right" : "left";
  return (
    <button
      onClick={onClick}
      style={{
        position: "relative",
        overflow: "hidden",
        background: selected
          ? "linear-gradient(145deg, rgba(182,88,255,0.18), rgba(0,224,255,0.08))"
          : "linear-gradient(145deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
        border: selected ? `1.5px solid ${accent}` : "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20,
        padding: "14px 14px 16px",
        cursor: "pointer",
        color: "#fff",
        fontFamily: "inherit",
        textAlign: dir === "rtl" ? "right" : "left",
        width: "100%",
        minHeight: 130,
        boxShadow: selected ? "0 0 24px rgba(182,88,255,0.25)" : "none",
        transition: "all 0.2s",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -8,
          [cornerKey]: -4,
          fontSize: 72,
          fontWeight: 800,
          letterSpacing: -3,
          color: accent,
          opacity: 0.22,
          fontFamily: "inherit",
          lineHeight: 1,
          pointerEvents: "none",
        }}
      >
        {badge}
      </div>
      {selected && (
        <div
          style={{
            position: "absolute",
            top: 12,
            [oppositeKey]: 12,
            width: 22,
            height: 22,
            borderRadius: 11,
            background: "#00E0FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 12px #00E0FF",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0C0719" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 6l3 3 5-6" />
          </svg>
        </div>
      )}
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: selected ? accent : "#fff", letterSpacing: -0.3 }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 2, lineHeight: 1.3 }}>{subtitle}</div>
        )}
        {footer && (
          <div
            style={{
              display: "inline-block",
              marginTop: 10,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: accent,
              background: `${accent}22`,
              padding: "4px 8px",
              borderRadius: 4,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </button>
  );
}
